import { EChartService } from '@/service/echart/echart.service';
import { CourseResult, StudentCompetencyGrowth } from '@/models/student/student-competency-growth';
import { DateUtils } from '@/utils/date.utils';
import i18n from '@/i18n';
import { ScoreUtils } from '@/utils/score.utils';

export class CompetencyGrowthEchartService extends EChartService {
  studentCompetencyGrowth: StudentCompetencyGrowth;
  isFunchart: boolean = true;

  private readonly TARGET_COLOR = '#5CADFF';
  private readonly LINE_COLOR = '#ED4014';
  private zoomStartValue: Date;
  private zoomEndValue: Date;

  constructor(studentCompetencyGrowth: StudentCompetencyGrowth) {
    super();
    this.series = [];

    // this.courseResults = courseResultsDesc.map((competencyResult: CourseResult) => new CourseResult().deserialize(competencyResult));
    this.studentCompetencyGrowth = new StudentCompetencyGrowth().deserialize(studentCompetencyGrowth);
    this.studentCompetencyGrowth.courseResults = this.studentCompetencyGrowth.courseResults.reverse();

    if (this.studentCompetencyGrowth.courseResults.length > 0) {
      this.zoomStartValue = this.studentCompetencyGrowth.courseResults[0].examDate;
      this.zoomEndValue = this.studentCompetencyGrowth.courseResults[this.studentCompetencyGrowth.courseResults.length - 1].examDate;
    }

    // 後でチャートを追加する用途で本オブジェクトを使用しないため、生成時にseriesも作る
    this.addSeries();
  }

  /**
   * AIGROW2-184 箱ひげ図にデータテーブルを追加
   * 箱ひげ図のみにデータを表形式で表示する
   * EChartService が箱ひげ図以外のグラフでも共通なので、空配列を返すだけのものを追加しています
   * subTableColmuns
   * subTableData
   */
  get subTableColmuns(): any[] {
    return [];
  }
  get subTableData(): any[] {
    return [];
  }

  // コンピテンシーごとの過去のスコア数に応じてX軸のラベルのフォントサイズを変更する
  get xLableFontSize(): number {
    switch (this.studentCompetencyGrowth.courseResults.length) {
      case 1:
        return 12;
      case 2:
        return 12;
      case 3:
        return 11;
      case 4:
        return 9;
      case 5:
        return 10;
    }
  }

  // コンピテンシーごとの過去のスコアの数が4件以上の場合はX軸のラベル名を斜め表示にする
  get xLableRotate(): number {
    switch (this.studentCompetencyGrowth.courseResults.length) {
      case 1:
      case 2:
      case 3:
        return 0;
      case 4:
      case 5:
        return 15;
    }
  }

  get options(): any {
    const self: any = this;
    return {
      title: { left: 'center', top: -5 },
      grid: { width: '75%', height: '60%', top: 30, right: 50 },
      xAxis: {
        type: 'category',
        //name: i18n.t('EXAM_DATE').toString(),
        position: 'bottom',
        data: this.studentCompetencyGrowth.courseResults.map((result: CourseResult) => DateUtils.changeFormatDate(result.examDate)),
        axisLabel: {
          formatter: '{value}',
          rotate: this.xLableRotate,
          fontSize: this.xLableFontSize,
        },
      },
      dataZoom: [
        {
          type: 'slider',
          // zoomLock: true,
          show: true,
          realtime: true,
          start: 0,
          end: 100,
          startValue: this.zoomStartValue,
          endValue: this.zoomEndValue,
          // filterMode: 'none',
          throttle: 0,
          // handleIcon: 'M10.7,11.9v-1.3H9.3v1.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4v1.3h1.3v-1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z',
          // handleSize: '80%',
          // handleStyle: {
          //   color: '#fff',
          //   shadowBlur: 3,
          //   shadowColor: 'rgba(0, 0, 0, 0.6)',
          //   shadowOffsetX: 2,
          //   shadowOffsetY: 2
          // },
          xAxisIndex: [0],
        },
        // {
        //   type: 'inside',
        //   show: true,
        //   height: 15,
        //   start: 1,
        //   end: 35,
        // },
      ],
      tooltip: {
        formatter: function(params: any) {
          const dataIndex: number = params.dataIndex;
          const courseResult: CourseResult = self.studentCompetencyGrowth.courseResults[dataIndex];
          const scoreProgress: string = ScoreUtils.toStringScoreProgressStatus(courseResult.giftCompletedCount, courseResult.isNewScore);
          const scoreProgressString: string = scoreProgress == '' ? '' : `(${scoreProgress})`;
          const title: string = i18n.t('EVAL_COURSE_NAME').toString() + ` : ${courseResult.title}`;
          const percentScore: string = i18n.t('SCORE').toString() + ` : ${courseResult.percentScore} ${scoreProgressString}`;
          const examDate: string = i18n.t('EXAM_DATE').toString() + ` : ${DateUtils.changeFormatDate(courseResult.examDate)}`;
          return [title, percentScore, examDate].join('<br/>');
        },
      },
      yAxis: this.isFunchart
        ? [
            {
              type: 'value',
              name: i18n.t('FLUCTUATION_PERCENT').toString() + '( % )',
              nameLocation: "end",
              nameTextStyle: {
                align: "left",
              },
              min: 0,
              max: 300,
            },
          ]
        : [
            {
              type: 'value',
              name: i18n.t('SCORE').toString(),
              min: 0,
            },
          ],
      series: this.series,
    };
  }

  addSeries() {
    this.series = [
      {
        type: 'line',
        symbolSize: 10,
        label: {
          normal: {
            show: true,
            position: 'top',
          },
        },
        name: this.studentCompetencyGrowth.competencyName,
        itemStyle: { color: this.TARGET_COLOR },
        data: this.studentCompetencyGrowth.courseResults.map((r: CourseResult) => r.percentScore),
      },
    ];
  }
}
